/* eslint-disable @typescript-eslint/no-explicit-any */
import { autoBatchEnhancer, combineReducers, configureStore } from '@reduxjs/toolkit';
import type {
  AnyAction,
  ConfigureStoreOptions,
  Middleware,
  Reducer,
  ReducersMapObject,
} from '@reduxjs/toolkit';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';

import type { PersistConfig } from 'redux-persist';

interface StoreFeature {
  enablePersistence?: boolean;
  persistConfig: PersistConfig<any, AnyAction>;
}

export const createMiddleware =
  (otherMiddleware: Middleware[], feature: StoreFeature) => (getDefaultMiddleware: any) => {
    let middleware: any = getDefaultMiddleware();

    if (feature.enablePersistence) {
      middleware = getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      });
    }

    otherMiddleware.forEach(m => {
      middleware = middleware.concat(m);
    });

    return middleware;
  };

export function createStore<R>(
  reducersInput: ReducersMapObject<R>,
  middleware: Middleware[],
  feature: StoreFeature,
  options?: ConfigureStoreOptions['preloadedState'] | undefined,
) {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const rootReducer = combineReducers(reducersInput);
  const reducer = feature.enablePersistence
    ? persistReducer(feature.persistConfig, rootReducer as Reducer)
    : rootReducer;

  const store = configureStore<R>({
    reducer,
    devTools: isDevelopment,
    middleware: createMiddleware(middleware, feature),
    enhancers: getDefaultEnhancers => getDefaultEnhancers().concat(autoBatchEnhancer()),
    ...options,
  });

  let persistor;
  if (feature.enablePersistence) {
    persistor = persistStore(store, null, () => {
      store.getState();
    });
  }

  return {
    store,
    persistor,
  };
}
